import { useState, useEffect } from "react";
import { Clock } from "lucide-react";
import { useTranslation } from "@/i18n";

interface CountdownTimerProps {
  time: Date;
  className?: string;
  compact?: boolean;
  upComing?: boolean;
}

const CountdownTimer = ({
  time,
  className = "",
  compact = false,
  upComing,
}: CountdownTimerProps) => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = time.getTime() - new Date().getTime();

      if (difference <= 0) {
        setIsExpired(true);
        return {
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
        };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    };

    // Initial calculation
    setTimeLeft(calculateTimeLeft());

    // Update every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    // Clean up interval on unmount
    return () => clearInterval(timer);
  }, [time]);

  if (isExpired) {
    return (
      <div
        className={`text-destructive flex items-center font-medium ${className}`}
      >
        <Clock className="mr-1 h-4 w-4" />
        {t("countdown.drawEnded")}
      </div>
    );
  }

  if (compact) {
    return (
      <div
        className={`flex items-center text-xs font-medium sm:text-sm ${className}`}
      >
        <Clock className="mr-1 h-3 w-3" />
        {timeLeft.days > 0 && t("countdown.daysShort", { days: timeLeft.days })}
        {String(timeLeft.hours).padStart(2, "0")}:
        {String(timeLeft.minutes).padStart(2, "0")}:
        {String(timeLeft.seconds).padStart(2, "0")}
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      <div className="mb-1 flex items-center text-sm font-medium">
        <Clock className="mr-1 h-4 w-4" />
        {upComing ? t("countdown.drawOpensIn") : t("countdown.closeDate")}
      </div>
      <div className="grid grid-cols-4 gap-1 text-center">
        <div className="bg-muted rounded p-1">
          <div className="text-lg font-bold">{timeLeft.days}</div>
          <div className="text-muted-foreground text-xs">
            {t("countdown.days")}
          </div>
        </div>
        <div className="bg-muted rounded p-1">
          <div className="text-lg font-bold">
            {String(timeLeft.hours).padStart(2, "0")}
          </div>
          <div className="text-muted-foreground text-xs">
            {t("countdown.hours")}
          </div>
        </div>
        <div className="bg-muted rounded p-1">
          <div className="text-lg font-bold">
            {String(timeLeft.minutes).padStart(2, "0")}
          </div>
          <div className="text-muted-foreground text-xs">
            {t("countdown.mins")}
          </div>
        </div>
        <div className="bg-muted rounded p-1">
          <div className="text-lg font-bold">
            {String(timeLeft.seconds).padStart(2, "0")}
          </div>
          <div className="text-muted-foreground text-xs">
            {t("countdown.secs")}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountdownTimer;
